import type { DropItem, FileDropItem } from "react-aria-components";

// Handling React Aria DropItems
// https://react-spectrum.adobe.com/react-aria/DropZone.html

/**
 * Collects all file items from one or more DropItems.
 *
 * This function processes DropItems which may include files, directories,
 * and nested subdirectories. It recursively traverses directory structures
 * and returns a flattened array containing FileDropItems with path information.
 *
 * @param itemOrItems - A single DropItem or an array of DropItems to process
 * @param currentPath - The current path being built during recursion
 * @returns A promise that resolves to a flattened array of FileDropItems with path information
 */

interface CollectFileDropItemsOptions {
  items: DropItem[];
  currentPath?: string;
}

export interface FileDropItemWithPath extends FileDropItem {
  fullPath: string;
}

export function collectFileDropItems({
  items,
  currentPath = "",
}: CollectFileDropItemsOptions): Promise<FileDropItemWithPath[]> {
  const promises = items
    .filter(item => item.kind === "file" || item.kind === "directory") // the "text" kind is ignored
    .map(async (item): Promise<FileDropItemWithPath[]> => {
      // Build the path for this item
      const itemPath = `${currentPath}/${item.name}`;

      // Handle single DropItem
      if (item.kind === "file") {
        // For file items, return the FileDropItem with path information
        return [
          {
            ...item,
            fullPath: itemPath,
          },
        ];
      }

      // Get all entries in the directory
      const fileItems: FileDropItemWithPath[] = [];
      for await (const entry of item.getEntries()) {
        // Recursively process each entry, passing the current path
        const entryFileItems = await collectFileDropItems({
          items: [entry],
          currentPath: itemPath,
        });
        fileItems.push(...entryFileItems);
      }
      return fileItems;
    });

  return Promise.all(promises).then(results => results.flat());
}
